import { ListChecks } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockRow, MockField, MockSelect, MockButton } from "../shared";

export default function AdminReferenceListsSection() {
  return (
    <div data-testid="guide-admin-reference-lists">
      <PageHeading
        icon={ListChecks}
        title="Reference Lists"
        description="The dropdown choices used across the app — priorities, origin types, document types, meeting types, and any other set list the Register form relies on."
      />

      <Callout variant="info" title="In plain words">
        Whenever a form shows you a dropdown to pick from, the options come from here. This screen is where you add, rename, reorder, or retire those choices.
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="super_admin" /><span className="text-xs text-white/55"><span className="text-white">manage_governance</span> on a draft version.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it when a dropdown needs a new option, a clearer label, or an option taken out of use. Like all governance changes, you edit a draft version first and activate it when ready.
        </p>
      </Section>

      <Section title="Adding or changing a list item">
        <Diagram
          frameLabel="Admin → Reference Lists (on a draft version)"
          legend={[
            <>Click <span className="text-white">Add Item</span>.</>,
            <>Choose which <span className="text-white">list</span> it belongs to (priority, origin type, document type…).</>,
            <>Set a stable <span className="text-white">item key</span> and the <span className="text-white">label</span> people see.</>,
            <>Click <span className="text-white">Save</span>, then activate the draft from Versions.</>,
          ]}
        >
          <MockButton label="Add Item" marker={1} />
          <MockSelect label="List key" value="priority" marker={2} />
          <MockRow>
            <MockField label="Item key" value="P1" marker={3} />
            <MockField label="Label" value="High priority" />
          </MockRow>
          <MockButton label="Save" marker={4} />
        </Diagram>
        <Steps steps={[
          <>On a draft version, Reference Lists tab → <span className="text-white">Add Item</span>.</>,
          <>Pick the list key, set item key + label + sort order, save.</>,
          <>Activate the draft from Versions when ready.</>,
        ]} />
      </Section>

      <Section title="Item fields">
        <div className="space-y-2">
          <Field name="List key" required="required"><p>Which vocabulary this item belongs to (<span className="text-white">priority</span>, <span className="text-white">origin_type</span>, <span className="text-white">doc_type</span>, etc).</p></Field>
          <Field name="Item key" required="required"><p>Stable internal identifier (e.g. <span className="text-white">P1</span>, <span className="text-white">BOARD_MEETING</span>). Items reference this key, so do not rename it once used.</p></Field>
          <Field name="Label" required="required"><p>Display text shown in the dropdown.</p></Field>
          <Field name="Sort order" required="optional"><p>Order within the list.</p></Field>
          <Field name="Active" required="optional"><p>Inactive items leave new dropdowns but stay on historical items.</p></Field>
        </div>
      </Section>

      <Callout variant="warning" title="Renaming an item key breaks history">
        Change the label freely; never change the item key once items reference it. If you need to retire an item, deactivate it instead.
      </Callout>
    </div>
  );
}
